import { Component, DestroyRef, inject, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDividerModule } from "@angular/material/divider";
import { MatButtonModule } from "@angular/material/button";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { toolbarOptions } from "../../shared/model/toolbar-constants";
import { FeatureFlags } from "../../viewer-components/model/feature-flags";
import { FeatureFlagsService } from "../../viewer-components/services/internal-services/feature-flags.service";
import { ToolbarService } from "../../viewer-components/services/internal-services/toolbar.service";
import { MapSearchLayerService } from "../../compound-components/services/layers/map-search-layer.service";

/**
 * Component for configuring heatmap layer properties in real-time
 * Provides controls for enabling/disabling the heatmap and adjusting radius and blur
 */
@Component({
  selector: "app-search-enhanced-heatmap-toolbar",
  imports: [
    CommonModule,
    FormsModule,
    MatSlideToggleModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    MatButtonModule,
  ],
  standalone: true,
  templateUrl: "./search-heatmap-config.component.html",
  styleUrls: ["./search-heatmap-config.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchHeatmapConfigComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  private flagService = inject(FeatureFlagsService);

  private toolbarService = inject(ToolbarService);

  private mapSearchLayerService = inject(MapSearchLayerService);

  toolbarOption = toolbarOptions.SEARCH_ENHANCED_HEATMAP_CONFIG;

  displayPanel = false;

  enabledFeature = false;

  // Heatmap configuration properties
  heatmapEnabled = true;

  heatmapRadius = 5;

  heatmapBlur = 15;

  // Slider configurations
  readonly RADIUS_MIN = 1;

  readonly RADIUS_MAX = 50;

  readonly BLUR_MIN = 1;

  readonly BLUR_MAX = 50;

  @Input() set enabledTool(tool: string) {
    this.displayPanel = this.toolbarOption === tool;
  }

  ngOnInit(): void {
    // Check if feature is enabled
    this.flagService
      .getFlags$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((flags: Record<string, any>) => {
        this.enabledFeature = flags[FeatureFlags.ENHANCED_GEO_SEARCH] || false;
      });

    // Initialize with current heatmap configuration
    this.loadCurrentHeatmapConfig();
  }

  /**
   * Toggle panel display state
   */
  togglePanelDisplay(): void {
    this.toolbarService.enableTool(!this.displayPanel ? this.toolbarOption : toolbarOptions.NONE);
  }

  /**
   * Load current heatmap configuration from the service
   */
  private loadCurrentHeatmapConfig(): void {
    const config = this.mapSearchLayerService.getHeatmapConfig();
    this.heatmapEnabled = config.enabled;
    this.heatmapRadius = config.radius;
    this.heatmapBlur = config.blur;
  }

  /**
   * Handle heatmap enabled state change
   */
  onHeatmapEnabledChange(): void {
    this.mapSearchLayerService.setHeatmapEnabled(this.heatmapEnabled);
  }

  /**
   * Handle heatmap radius change
   */
  onRadiusChange(): void {
    this.mapSearchLayerService.setHeatmapRadius(this.heatmapRadius);
  }

  /**
   * Handle heatmap blur change
   */
  onBlurChange(): void {
    this.mapSearchLayerService.setHeatmapBlur(this.heatmapBlur);
  }

  /**
   * Reset heatmap configuration to default values
   */
  resetToDefaults(): void {
    this.heatmapEnabled = true;
    this.heatmapRadius = 5;
    this.heatmapBlur = 15;
    this.mapSearchLayerService.updateHeatmapConfig({
      enabled: this.heatmapEnabled,
      radius: this.heatmapRadius,
      blur: this.heatmapBlur,
    });
  }
}
